import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NewsletterConfig } from '../../Data/NewsletterBrands';

/**
 * useNewsletterSignup
 * @description This hook preloads the images for the newsletter signup component based on the selected newsletter brand. It automatically updates when the relevant newsletter configuration changes.
 * @returns {void}
 */
const useNewsletterSignup = () => {
  const components = useSelector((store) => store.components);
  const { fontOverride } = useSelector((store) => store.optionalFormConfig);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const [imagesToPreload, setImagesToPreload] = useState([]);

  /**
   * useEffect
   * @description This will run when imagesToPreload changes, and will preload the assets for the newsletter signup component
   */
  useEffect(() => {
    const link = document.createElement('link');
    const preloadedImages = [];

    if (imagesToPreload.length > 0) {
      // If font override is already Public Sans there is no need to load the font twice.
      if (fontOverride?.name !== 'Public Sans') {
        link.rel = 'stylesheet';
        link.href =
          'https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;700;900&display=swap';
        link.type = 'text/css';

        document.head.appendChild(link);
      }

      imagesToPreload.forEach((image) => {
        const preloadedImage = document.createElement('link');
        preloadedImage.rel = 'preload';
        preloadedImage.href = image;
        preloadedImage.as = 'image';

        document.head.appendChild(preloadedImage);

        preloadedImages.push(preloadedImage);
      });
    }

    return () => {
      if (preloadedImages.length > 0) {
        preloadedImages.forEach((image) => {
          if (document.head.contains(image)) {
            document.head.removeChild(image);
          }
        });
      }
    };
  }, [imagesToPreload]);

  useEffect(() => {
    const newsletterComponents = Object.values(components).filter(
      (component) => {
        return component.component.type === 'newsletter-signup';
      }
    );

    const newsletterComponentBrandsIds = newsletterComponents.map(
      (component) => {
        if (
          component.component.additionalSettings.is_dynamic &&
          dynamicNewsletterName
        ) {
          return dynamicNewsletterName;
        }
        return component.component.additionalSettings.brand;
      }
    );
    const brandImages = newsletterComponentBrandsIds
      .filter((brandId) => brandId !== 'none')
      .map((brandId) => {
        return NewsletterConfig[brandId].image;
      });

    setImagesToPreload(brandImages);
  }, [dynamicNewsletterName]);
};

export default useNewsletterSignup;
